import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocalization } from '../contexts/LocalizationContext';
import { getAssetUrl } from '../services/api';
import GuestNavbar from './GuestNavbar';
import './LandingPage.css';

const LandingPage = ({ onLanguageSelected, onGetStarted, hideNavbar = false }) => {
  const { t } = useLocalization();

  const handleContinue = () => {
    onGetStarted();
  };


  return (
    <div className="content-wrapper">
      {!hideNavbar && (
        <GuestNavbar 
          onGetStarted={handleContinue}
          showGetStarted={true} 
          showAbout={true}
        />
      )}

      <section className="wrapper bg-soft-primary hero-section">
        <div className="container pt-4 pt-md-6 pb-2 pb-md-n5">
          <div className="row gx-md-8 gx-lg-12 gy-3 gy-lg-0 mb-6">
            <div className="col-lg-6">
              <h1 className="display-1 fs-66 lh-xxs mb-0">
                {t('landing.heroTitle') || 'We bring rapid solutions for math learning.'}
              </h1>
            </div>
            <div className="col-lg-6">
              <p className="lead fs-25 my-3">
                {t('landing.heroSubtitle') || 'Interactive math learning platform for students aged 7-15 with gamified challenges, stars, and achievement system.'}
              </p>
            </div>
          </div>
          <div className="position-relative">
            <div className="shape bg-dot primary rellax w-17 h-21" data-rellax-speed="1" style={{top: '-2.5rem', right: '-2.7rem'}}></div>
            <figure className="rounded mb-md-0">
              <img 
                src={getAssetUrl("/assets/img/photos/image-01.png")} 
                alt="Math Learning Platform"
                className="rounded"
                style={{
                  width: '100%',
                  height: 'auto',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
                }}
                onError={(e) => {
                  console.error('Failed to load image:', e.target.src);
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hero-placeholder" style={{display: 'none'}}>
                <div className="math-symbols">
                  <span className="symbol">🔢</span>
                  <span className="symbol">📐</span>
                  <span className="symbol">⭐</span>
                  <span className="symbol">🎯</span>
                  <span className="symbol">🏆</span>
                </div>
              </div>
            </figure>
          </div>
        </div>
      </section>

      <section className="wrapper bg-light">
        <div className="container pt-8 pt-md-12 pb-14 pb-md-17">
          {/* Master Advanced Concepts Section */}
          <div className="row gx-lg-8 gx-xl-12 gy-10 mb-14 mb-md-17 align-items-center">
            <div className="col-lg-6 order-lg-2 position-relative">
              <div className="shape rounded-circle bg-soft-primary rellax w-18 h-18" data-rellax-speed="1" style={{bottom: '-2.5rem', right: '-1.5rem'}}></div>
              <figure className="rounded mb-0">
                <img 
                  src={getAssetUrl("/assets/img/photos/image-02.png")} 
                  alt="Advanced Concepts"
                  className="rounded"
                  style={{
                    width: '100%',
                    height: 'auto'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="feature-placeholder" style={{display: 'none'}}>
                  <div className="feature-icon">🧠</div>
                  <h4>Advanced Concepts</h4>
                </div>
              </figure>
            </div>
            <div className="col-lg-6 order-lg-1">
              <h3 className="display-6 mb-4">
                {t('landing.advancedConcepts') || 'Master advanced concepts'}
              </h3>
              <p className="mb-5">
                {t('landing.advancedDescription') || 'Children solve challenges by topic, from easier to harder problems. With a step by step approach, children feel challenged but not overwhelmed, developing a higher level of understanding of advanced topics in counting, logic and arithmetic.'}
              </p>
              <div className="row gy-3">
                <div className="col-xl-6">
                  <ul className="icon-list bullet-bg bullet-soft-leaf mb-0">
                    <li>
                      <span><i className="uil uil-check"></i></span>
                      <span>{t('landing.concept1') || 'Pattern recognition'}</span>
                    </li>
                    <li className="mt-3">
                      <span><i className="uil uil-check"></i></span>
                      <span>{t('landing.concept2') || 'Detective puzzles'}</span>
                    </li>
                  </ul>
                </div>
                <div className="col-xl-6">
                  <ul className="icon-list bullet-bg bullet-soft-leaf mb-0">
                    <li>
                      <span><i className="uil uil-check"></i></span>
                      <span>{t('landing.concept3') || 'Clock problems'}</span>
                    </li>
                    <li className="mt-3">
                      <span><i className="uil uil-check"></i></span>
                      <span>{t('landing.concept4') || 'Many more'}</span>
                    </li>
                  </ul>
                </div>
              </div>
              <button className="btn btn-soft-leaf rounded-pill mt-6 mb-0" onClick={handleContinue}>
                {t('landing.joinButton') || 'Join'}
              </button>
            </div>
          </div>

          {/* For Parents Section */}
          <div className="row mb-14 mb-md-17 text-center">
            <div className="col-lg-8 col-xl-7 mx-auto">
              <h2 className="fs-16 text-uppercase text-primary mb-3">
                {t('landing.forParents') || 'For Parents'}
              </h2>
              <p className="lead fs-25">
                {t('landing.parentsDescription') || 'We provide a curriculum for studying advanced math for elementary school, ages 7 to 14. The games are suitable for children that are advanced and like to problem solve. Children solve bite-sized tasks to get stars, collect badges and unlock future challenges. Oh, there are dragons too!'}
              </p>
              <button className="btn btn-primary rounded-pill mt-4" onClick={handleContinue}>
                {t('landing.joinButton') || 'Join'}
              </button>
            </div>
          </div>

          {/* For Teachers Section */}
          <div className="row text-center">
            <div className="col-lg-8 col-xl-7 mx-auto">
              <h2 className="fs-16 text-uppercase text-primary mb-3">
                {t('landing.forTeachers') || 'For Teachers'}
              </h2>
              <p className="lead fs-25">
                {t('landing.teachersDescription') || 'Login to get access to our material - printable study lessons, homework assignments and solutions. Tutoring your next class can be as easy as sharing these materials and encouraging students to work through the challenges. After class, children can login into our platform to further practice the themes that were started in class, solidifying their understanding.'}
              </p>
              <button className="btn btn-primary rounded-pill mt-4" onClick={handleContinue}>
                {t('landing.joinButton') || 'Join'}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section 
        className="wrapper image-wrapper bg-image bg-overlay" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${getAssetUrl('/assets/img/photos/image-04.png')}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="container py-18">
          <div className="row text-center">
            <div className="col-lg-11 col-xl-10 col-xxl-8 mx-auto">
              <h2 className="fs-16 text-uppercase text-white mb-3">
                {t('landing.joinCommunity') || 'Join Our Community'}
              </h2>
              <h3 className="display-4 mb-6 text-white px-lg-5 px-xxl-0">
                {t('landing.communityDescription') || 'We are trusted by over 5000+ students. Join them by using our platform and grow your math skills.'}
              </h3>
              <button className="btn btn-white rounded-pill mb-0 text-nowrap" onClick={handleContinue}>
                {t('landing.joinUs') || 'Join Us'}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;